/**
 * Whiteboard Page Component - User Platform
 * 
 * A collaborative whiteboard that users can join via a shared link.
 * Mobile-friendly design.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import styled from 'styled-components';
import LogoLoader from '../LogoLoader';
import { ReactSketchCanvas } from 'react-sketch-canvas';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { socket, onPrivateMessageReceived, sendPrivateMessage } from '../../socket';

// API base URL - whiteboard API is on the Expert Platform Server, not the User Platform Server
// Use REACT_APP_EXPERT_SERVER if available, otherwise fall back to production URL
const API_BASE = (process.env.REACT_APP_EXPERT_SERVER || 'https://sea-lion-app-7wbpx.ondigitalocean.app').replace(/\/$/, '');

// ============================================
// STYLED COMPONENTS
// ============================================

const PageContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  overflow: hidden;
  box-sizing: border-box;

  @media (max-width: 768px) {
    height: calc(100vh - 100px);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: linear-gradient(135deg, #ffffff 0%, #ffffff 100%);
  border-bottom: 1px solid rgba(91, 97, 234, 0.2);
  z-index: 100;
  min-height: 50px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 8px 12px;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const Title = styled.h1`
  font-size: 16px;
  font-weight: 600;
  color: #192947;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const ParticipantsBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #192947;
  background: rgba(18, 165, 113, 0.1);
  padding: 4px 10px;
  border-radius: 20px;
  border: 1px solid rgba(18, 165, 113, 0.2);
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 3px 8px;
    font-size: 11px;
  }
`;

const StatusIndicator = styled.div`
  font-size: 11px;
  color: ${props => props.$saving ? '#a2611c' : '#0f8f63'};
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.9);
  border-bottom: 1px solid rgba(91, 97, 234, 0.1);
  flex-wrap: wrap;

  @media (max-width: 768px) {
    padding: 6px 12px;
    gap: 6px;
    justify-content: center;
  }
`;

const ToolGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 8px;
  border-right: 1px solid rgba(91, 97, 234, 0.2);

  &:last-child {
    border-right: none;
  }

  @media (max-width: 768px) {
    padding: 0 4px;
    border-right: none;
  }
`;

const ColorPicker = styled.input`
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  background: transparent;
  padding: 0;

  &::-webkit-color-swatch-wrapper {
    padding: 2px;
  }

  &::-webkit-color-swatch {
    border-radius: 4px;
    border: 2px solid #cad5e9;
  }

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
  }
`;

const SizeSlider = styled.input`
  width: 80px;
  height: 4px;
  accent-color: #5b61ea;
  cursor: pointer;

  @media (max-width: 768px) {
    width: 60px;
  }
`;

const SizeLabel = styled.span`
  font-size: 11px;
  color: #192947;
  min-width: 24px;
`;

const ToolButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
  background: ${props => props.$active ? 'rgba(91, 97, 234, 0.3)' : 'rgba(91, 97, 234, 0.1)'};
  border: 1px solid ${props => props.$active ? 'rgba(91, 97, 234, 0.5)' : 'rgba(91, 97, 234, 0.2)'};
  color: #192947;

  &:hover {
    background: rgba(91, 97, 234, 0.2);
  }

  @media (max-width: 768px) {
    padding: 5px 8px;
    font-size: 11px;
  }
`;

const CanvasContainer = styled.div`
  flex: 1;
  position: relative;
  background: #ffffff;
  overflow: hidden;
`;

const ShapeOverlay = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: ${props => props.$active ? 'auto' : 'none'};
  z-index: ${props => props.$active ? 10 : 5};
`;

const ToolSeparator = styled.div`
  width: 1px;
  height: 24px;
  background: rgba(91, 97, 234, 0.3);
  margin: 0 4px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: #ffffff;
  color: #192947;
  gap: 16px;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: #ffffff;
  color: #192947;
  text-align: center;
  padding: 20px;
`;

const ErrorIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  
  ${props => props.$primary ? `
    background: linear-gradient(135deg, #5b61ea 0%, #885de8 100%);
    border: none;
    color: #fff;
    
    &:hover {
      box-shadow: 0 4px 15px rgba(91, 97, 234, 0.4);
    }
  ` : `
    background: rgba(91, 97, 234, 0.1);
    border: 1px solid rgba(91, 97, 234, 0.3);
    color: #192947;
    
    &:hover {
      background: rgba(91, 97, 234, 0.2);
    }
  `}
`;

// ============================================
// MAIN COMPONENT
// ============================================

const WhiteboardPage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth0();
  const authenticatedAxios = useAuthenticatedAxios();
  
  const canvasRef = useRef(null);
  const shapeOverlayRef = useRef(null);
  const [whiteboard, setWhiteboard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [strokeColor, setStrokeColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [isEraser, setIsEraser] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef(null);
  
  // Shape drawing state
  const [activeTool, setActiveTool] = useState('pen'); // 'pen', 'line', 'rectangle', 'circle', 'triangle'
  const [shapes, setShapes] = useState([]); // Completed shapes
  const [currentShape, setCurrentShape] = useState(null); // Shape being drawn
  const [isDrawingShape, setIsDrawingShape] = useState(false);
  const [shapeStartPoint, setShapeStartPoint] = useState(null);

  // Load whiteboard data
  useEffect(() => {
    const loadWhiteboard = async () => {
      try {
        setIsLoading(true);
        
        const apiUrl = `${API_BASE}/api/whiteboard/${roomId}`;
        console.log('[Whiteboard] User loading from URL:', apiUrl);
        
        const response = await fetch(apiUrl);
        console.log('[Whiteboard] Response status:', response.status);
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('Whiteboard not found. It may have been closed or deleted.');
          } else {
            setError('Failed to load whiteboard.');
          }
          return;
        }
        
        const data = await response.json();
        console.log('[Whiteboard] Loaded whiteboard data:', data.whiteboard?.roomId);
        console.log('[Whiteboard] Scene data paths count:', data.whiteboard?.sceneData?.paths?.length || 0);
        setWhiteboard(data.whiteboard);
        
        // Load saved paths if available - use setTimeout to ensure canvas is mounted
        // Set isReceivingUpdate to prevent the load from triggering a save
        // Load shapes if they exist
        if (data.whiteboard?.sceneData?.shapes?.length > 0) {
          setShapes(data.whiteboard.sceneData.shapes);
          console.log('[Whiteboard] Loaded', data.whiteboard.sceneData.shapes.length, 'shapes');
        }
        
        if (data.whiteboard?.sceneData?.paths?.length > 0) {
          isReceivingUpdate.current = true;
          setTimeout(() => {
            if (canvasRef.current) {
              console.log('[Whiteboard] Loading saved paths to canvas');
              canvasRef.current.loadPaths(data.whiteboard.sceneData.paths);
            } else {
              console.warn('[Whiteboard] Canvas ref not ready for loading paths');
            }
            // Keep the flag true for a bit longer to let canvas settle
            setTimeout(() => {
              isReceivingUpdate.current = false;
              console.log('[Whiteboard] Ready for new drawings');
            }, 500);
          }, 100);
        }
        
        // Join the whiteboard (NOT create - just join existing)
        const userName = user?.name || user?.email || 'Guest';
        const odId = user?.sub || `guest-${Date.now()}`;
        
        console.log('[Whiteboard] Joining existing whiteboard:', roomId);
        
        const joinResponse = await fetch(`${API_BASE}/api/whiteboard/${roomId}/join`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ odId, name: userName })
        });
        
        console.log('[Whiteboard] Join response status:', joinResponse.status);
        
        // Update whiteboard state with the response (includes updated participant list)
        if (joinResponse.ok) {
          const joinData = await joinResponse.json();
          if (joinData.whiteboard) {
            setWhiteboard(joinData.whiteboard);
            console.log('[Whiteboard] Updated participants:', joinData.whiteboard.participants?.length);
          }
        }
        
      } catch (err) {
        console.error('Error loading whiteboard:', err);
        setError('Failed to connect to whiteboard. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    if (roomId && !authLoading) {
      loadWhiteboard();
    }
  }, [roomId, user, authLoading]);

  // Track if we're receiving an update to avoid echo
  const isReceivingUpdate = useRef(false);
  
  // Track if initial sync was done
  const initialSyncDone = useRef(false);

  // Sync function to fetch latest changes from server
  const syncWhiteboard = useCallback(async () => {
    if (!roomId || !canvasRef.current) return;
    
    try {
      console.log('[Whiteboard] Syncing with server...');
      const response = await fetch(`${API_BASE}/api/whiteboard/${roomId}`);
      if (response.ok) {
        const data = await response.json();
        setWhiteboard(data.whiteboard);
        
        // Load shapes from server
        if (data.whiteboard?.sceneData?.shapes) {
          setShapes(data.whiteboard.sceneData.shapes);
        }
        
        // Load the latest paths from server
        if (data.whiteboard?.sceneData?.paths) {
          isReceivingUpdate.current = true;
          await canvasRef.current.clearCanvas();
          await canvasRef.current.loadPaths(data.whiteboard.sceneData.paths);
          setTimeout(() => { isReceivingUpdate.current = false; }, 100);
        }
        console.log('[Whiteboard] Synced with server');
      }
    } catch (err) {
      console.error('Error syncing whiteboard:', err);
    }
  }, [roomId]);

  // One-time auto-sync when page loads
  useEffect(() => {
    if (!isLoading && whiteboard && canvasRef.current && !initialSyncDone.current) {
      initialSyncDone.current = true;
      console.log('[Whiteboard] Running initial sync...');
      syncWhiteboard();
    }
  }, [isLoading, whiteboard, syncWhiteboard]);

  // Listen for whiteboard updates via WebSocket
  useEffect(() => {
    if (!roomId) return;

    console.log('[Whiteboard] Setting up WebSocket listener for room:', roomId);
    
    const handleWhiteboardUpdate = async (data) => {
      // Only process whiteboard updates
      if (data.type !== 'whiteboard_update') return;
      
      try {
        // Parse content if it's a string
        const content = typeof data.content === 'string' ? JSON.parse(data.content) : data.content;
        
        // Only process if it's for our room and not from us
        if (content?.roomId === roomId && 
            data.senderId !== user?.sub &&
            canvasRef.current) {
          console.log('[Whiteboard] Received update via WebSocket from:', data.senderId);
          isReceivingUpdate.current = true;
          
          // Load shapes from WebSocket update
          if (content?.shapes) {
            setShapes(content.shapes);
          }
          
          await canvasRef.current.clearCanvas();
          if (content?.paths) {
            await canvasRef.current.loadPaths(content.paths);
          }
          
          setTimeout(() => { isReceivingUpdate.current = false; }, 100);
        }
      } catch (err) {
        console.error('Error applying whiteboard update:', err);
        isReceivingUpdate.current = false;
      }
    };

    const cleanup = onPrivateMessageReceived(handleWhiteboardUpdate);
    return cleanup;
  }, [roomId, user?.sub]);

  // Auto-save with debounce and broadcast via WebSocket
  const saveWhiteboard = useCallback(async () => {
    if (!canvasRef.current || !roomId || isReceivingUpdate.current) return;
    
    try {
      setIsSaving(true);
      const paths = await canvasRef.current.exportPaths();
      
      // Don't save empty canvas if whiteboard already had data
      // This prevents accidental data loss during load/sync
      if ((!paths || paths.length === 0) && whiteboard?.sceneData?.paths?.length > 0) {
        console.log('[Whiteboard] Skipping save - empty paths but DB has data');
        setIsSaving(false);
        return;
      }
      
      const sceneData = {
        paths,
        shapes, // Include shapes in save data
        updatedAt: new Date().toISOString()
      };
      
      // Save to database (Expert Platform Server)
      console.log('[Whiteboard] Saving to:', `${API_BASE}/api/whiteboard/${roomId}`, 'paths count:', paths?.length || 0);
      const saveResponse = await fetch(`${API_BASE}/api/whiteboard/${roomId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sceneData })
      });
      
      if (!saveResponse.ok) {
        console.error('[Whiteboard] Save failed:', saveResponse.status, await saveResponse.text());
      } else {
        console.log('[Whiteboard] Save successful');
      }

      // Broadcast update via WebSocket to other participants
      if (whiteboard?.participants) {
        whiteboard.participants.forEach(participant => {
          if (participant.odId !== user?.sub) {
            sendPrivateMessage({
              threadId: roomId,
              type: 'whiteboard_update',
              content: JSON.stringify({ roomId, paths, shapes }),
              receiverId: participant.odId,
              senderId: user?.sub,
              time: new Date().toISOString()
            });
          }
        });
        console.log('[Whiteboard] Broadcasted update to participants');
      }
      
    } catch (err) {
      console.error('Error saving whiteboard:', err);
    } finally {
      setIsSaving(false);
    }
  }, [roomId, shapes, whiteboard?.participants, user?.sub]);

  // Handle canvas changes
  const handleCanvasChange = useCallback(() => {
    if (isReceivingUpdate.current) return;
    
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(saveWhiteboard, 500); // Faster for real-time feel
  }, [saveWhiteboard]);

  // Toggle eraser mode
  const toggleEraser = () => {
    setIsEraser(!isEraser);
  };

  // Select tool
  const selectTool = (tool) => {
    setActiveTool(tool);
    setIsEraser(false);
    if (canvasRef.current) {
      canvasRef.current.eraseMode(false);
    }
  };

  // Get mouse/touch position relative to overlay
  const getMousePosition = (e) => {
    const rect = shapeOverlayRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    
    // Handle both mouse and touch events
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  // Shape mouse/touch handlers
  const handleShapeStart = (e) => {
    if (activeTool === 'pen') return;
    e.preventDefault();
    const pos = getMousePosition(e);
    setShapeStartPoint(pos);
    setIsDrawingShape(true);
    setCurrentShape({
      type: activeTool,
      startX: pos.x,
      startY: pos.y,
      endX: pos.x,
      endY: pos.y,
      color: strokeColor,
      width: strokeWidth
    });
  };

  const handleShapeMove = (e) => {
    if (!isDrawingShape || activeTool === 'pen') return;
    e.preventDefault();
    const pos = getMousePosition(e);
    setCurrentShape(prev => ({
      ...prev,
      endX: pos.x,
      endY: pos.y
    }));
  };

  const handleShapeEnd = () => {
    if (!isDrawingShape || !currentShape) return;
    setShapes(prev => [...prev, { ...currentShape, id: Date.now() }]);
    setCurrentShape(null);
    setIsDrawingShape(false);
    setShapeStartPoint(null);
    // Save will be triggered by the useEffect watching shapes
  };
  
  // Track previous shapes length to detect new shapes added
  const prevShapesLengthRef = useRef(shapes.length);
  
  // Auto-save when shapes change (not from receiving updates)
  useEffect(() => {
    // Only trigger save if shapes were added (not cleared or received from sync)
    if (shapes.length > prevShapesLengthRef.current && !isReceivingUpdate.current) {
      console.log('[Whiteboard] Shape added, triggering save');
      saveWhiteboard();
    }
    prevShapesLengthRef.current = shapes.length;
  }, [shapes, saveWhiteboard]);

  // Render a shape as SVG element
  const renderShape = (shape, isPreview = false) => {
    const { type, startX, startY, endX, endY, color, width, id } = shape;
    const key = isPreview ? 'preview' : id;
    const opacity = isPreview ? 0.6 : 1;
    
    switch (type) {
      case 'line':
        return (
          <line
            key={key}
            x1={startX}
            y1={startY}
            x2={endX}
            y2={endY}
            stroke={color}
            strokeWidth={width}
            opacity={opacity}
          />
        );
      case 'rectangle':
        const rectX = Math.min(startX, endX);
        const rectY = Math.min(startY, endY);
        const rectW = Math.abs(endX - startX);
        const rectH = Math.abs(endY - startY);
        return (
          <rect
            key={key}
            x={rectX}
            y={rectY}
            width={rectW}
            height={rectH}
            stroke={color}
            strokeWidth={width}
            fill="none"
            opacity={opacity}
          />
        );
      case 'circle':
        const centerX = (startX + endX) / 2;
        const centerY = (startY + endY) / 2;
        const radiusX = Math.abs(endX - startX) / 2;
        const radiusY = Math.abs(endY - startY) / 2;
        return (
          <ellipse
            key={key}
            cx={centerX}
            cy={centerY}
            rx={radiusX}
            ry={radiusY}
            stroke={color}
            strokeWidth={width}
            fill="none"
            opacity={opacity}
          />
        );
      case 'triangle':
        const triTopX = (startX + endX) / 2;
        const triTopY = Math.min(startY, endY);
        const triBottomY = Math.max(startY, endY);
        const triLeftX = Math.min(startX, endX);
        const triRightX = Math.max(startX, endX);
        const points = `${triTopX},${triTopY} ${triLeftX},${triBottomY} ${triRightX},${triBottomY}`;
        return (
          <polygon
            key={key}
            points={points}
            stroke={color}
            strokeWidth={width}
            fill="none"
            opacity={opacity}
          />
        );
      default:
        return null;
    }
  };

  // Note: Clear functionality removed from User Platform - only Expert can clear

  if (isLoading || authLoading) {
    return (
      <LoadingContainer>
        <LogoLoader size={180} message="Opening the whiteboard" />
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <ErrorIcon>😕</ErrorIcon>
        <h2>Oops!</h2>
        <p>{error}</p>
        <Button $primary onClick={() => navigate('/conversations')} style={{ marginTop: '20px' }}>
          Go Back
        </Button>
      </ErrorContainer>
    );
  }

  // Check if whiteboard is inactive
  if (whiteboard && !whiteboard.isActive) {
    return (
      <ErrorContainer>
        <ErrorIcon>📋</ErrorIcon>
        <h2>Whiteboard Closed</h2>
        <p>This whiteboard session has ended.</p>
        <Button $primary onClick={() => navigate('/conversations')} style={{ marginTop: '20px' }}>
          Go Back
        </Button>
      </ErrorContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <HeaderLeft>
          <Title>
            🎨 {whiteboard?.title || 'Whiteboard'}
          </Title>
          <ParticipantsBadge style={{ background: 'rgba(91, 97, 234, 0.2)', color: '#5874be' }}>
            🔗 {roomId}
          </ParticipantsBadge>
          <ParticipantsBadge>
            👥 {whiteboard?.participants?.length || 1}
          </ParticipantsBadge>
          <StatusIndicator $saving={isSaving}>
            {isSaving ? '💾 Saving...' : '✓ Saved'}
          </StatusIndicator>
        </HeaderLeft>
      </Header>

      <Toolbar>
        <ToolGroup>
          <span style={{ color: '#192947', fontSize: '12px' }}>Tools:</span>
          <ToolButton $active={activeTool === 'pen'} onClick={() => selectTool('pen')} title="Pen">
            ✏️
          </ToolButton>
          <ToolButton $active={activeTool === 'line'} onClick={() => selectTool('line')} title="Line">
            📏
          </ToolButton>
          <ToolButton $active={activeTool === 'rectangle'} onClick={() => selectTool('rectangle')} title="Rectangle">
            ⬜
          </ToolButton>
          <ToolButton $active={activeTool === 'circle'} onClick={() => selectTool('circle')} title="Circle">
            ⭕
          </ToolButton>
          <ToolButton $active={activeTool === 'triangle'} onClick={() => selectTool('triangle')} title="Triangle">
            🔺
          </ToolButton>
        </ToolGroup>

        <ToolSeparator />

        <ToolGroup>
          <span style={{ color: '#192947', fontSize: '12px' }}>Color:</span>
          <ColorPicker
            type="color"
            value={strokeColor}
            onChange={(e) => {
              setStrokeColor(e.target.value);
              setIsEraser(false);
            }}
          />
        </ToolGroup>

        <ToolGroup>
          <span style={{ color: '#192947', fontSize: '12px' }}>Size:</span>
          <SizeSlider
            type="range"
            min="1"
            max="20"
            value={strokeWidth}
            onChange={(e) => setStrokeWidth(parseInt(e.target.value))}
          />
          <SizeLabel>{strokeWidth}px</SizeLabel>
        </ToolGroup>

        <ToolSeparator />

        <ToolGroup>
          <ToolButton $active={isEraser} onClick={toggleEraser}>
            🧹
          </ToolButton>
          <ToolButton onClick={() => canvasRef.current?.undo()}>
            ↩️
          </ToolButton>
          <ToolButton onClick={() => canvasRef.current?.redo()}>
            ↪️
          </ToolButton>
          {/* Clear buttons removed - only Expert can clear the whiteboard */}
          <ToolButton onClick={syncWhiteboard} title="Sync with other participants">
            🔄
          </ToolButton>
        </ToolGroup>
      </Toolbar>

      <CanvasContainer>
        <ReactSketchCanvas
          ref={canvasRef}
          strokeWidth={strokeWidth}
          strokeColor={isEraser ? '#ffffff' : strokeColor}
          canvasColor="#ffffff"
          style={{ 
            width: '100%', 
            height: '100%',
            pointerEvents: activeTool === 'pen' ? 'auto' : 'none'
          }}
          onChange={handleCanvasChange}
        />
        <ShapeOverlay
          ref={shapeOverlayRef}
          $active={activeTool !== 'pen'}
          onMouseDown={handleShapeStart}
          onMouseMove={handleShapeMove}
          onMouseUp={handleShapeEnd}
          onMouseLeave={handleShapeEnd}
          onTouchStart={handleShapeStart}
          onTouchMove={handleShapeMove}
          onTouchEnd={handleShapeEnd}
        >
          {/* Render completed shapes */}
          {shapes.map(shape => renderShape(shape))}
          {/* Render current shape being drawn */}
          {currentShape && renderShape(currentShape, true)}
        </ShapeOverlay>
      </CanvasContainer>
    </PageContainer>
  );
};

export default WhiteboardPage;

